import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useSearchParams } from 'react-router-dom'
import { get } from 'lodash'
import { MessageSquareMore, PanelRightClose, X } from 'lucide-react'

import { CommentSidebar, triggerToast } from '@/components'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tab'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useDocumentViewAudit } from '@/features/audit'
import {
  getDefaultVersionId,
  useDocumentRetrieval,
  useDocumentStore,
  useVersionList,
} from '@/features/document'
import {
  ComparisonLayoutDialog,
  VersionSelectionDialog,
} from '@/features/version-comparison/components'
import { CommentDetail } from '@/features/view-tfl-uploaded/components/Thread'
import {
  useCommentStore,
  useTflViewerStore,
} from '@/features/view-tfl-uploaded/store'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { ResponseThread } from '@/schema'

import { extractMentionedUserIds } from '../features/view-tfl-uploaded/utils'
import { useCurrentUser } from '../hooks/useAuth'
import { cn } from '../utils'

import { DocumentViewer } from './TFLViewer/DocumentViewer'
import { DocumentTOC } from './TFLViewer'

const DEFAULT_TAB = 'toc'

// Timing constants for smooth scroll and UI updates
const SCROLL_DELAY_SIDEBAR = 100
const SCROLL_DELAY_VIEWER = 300
const DETAIL_PANEL_DELAY = 300

// Navigation state type for document data
interface DocumentNavigationState {
  document?: { id: string; filename: string }
  activeDocumentId?: string
  versionId?: string
  commentId?: string
  replyId?: string
  // Flag to check if the navigation is from notification
  isNotification?: boolean
  // Flag to check if the navigation is from mentioned notification
  isMentioned?: boolean
}

interface ExtractedNavContext {
  docId: string
  filename: string
  versionId: string | null
  commentId: string | null
  replyId: string | null
  isNotification: boolean
  isMentioned: boolean
  isQueryParam: boolean
  isNavState: boolean
}

const extractNavigationContext = (
  searchParams: URLSearchParams,
  documentRetrievalData: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  navigationState: DocumentNavigationState | null
): ExtractedNavContext | null => {
  const queryDocumentId = searchParams.get('documentId')
  const stateDocumentId =
    get(navigationState, 'document.id') ||
    get(navigationState, 'activeDocumentId')

  let mode = 'NONE'
  if (queryDocumentId) {
    mode = 'QUERY'
  } else if (navigationState && stateDocumentId) {
    mode = 'STATE'
  }

  switch (mode) {
    case 'QUERY':
      if (!documentRetrievalData) return null
      return {
        docId: queryDocumentId as string,
        filename:
          documentRetrievalData.filename ||
          documentRetrievalData.display_name ||
          'Document',
        versionId: searchParams.get('versionId'),
        commentId: searchParams.get('commentId'),
        replyId: searchParams.get('replyId'),
        isNotification: false,
        isMentioned: false,
        isQueryParam: true,
        isNavState: false,
      }

    case 'STATE': {
      const document = get(navigationState, 'document')
      if (!document?.id) return null

      return {
        docId: document.id,
        filename: document.filename,
        versionId: get(navigationState, 'versionId', null),
        commentId: get(navigationState, 'commentId', null),
        replyId: get(navigationState, 'replyId', null),
        isNotification: !!navigationState?.isNotification,
        isMentioned: !!navigationState?.isMentioned,
        isQueryParam: false,
        isNavState: true,
      }
    }

    default:
      return null
  }
}

const TruncatedText = ({ text }: { text: string }) => {
  const cellRef = useRef<HTMLSpanElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  useEffect(() => {
    const checkOverflow = () => {
      const element = cellRef.current
      if (element) {
        const isOverflow = element.scrollWidth > element.clientWidth
        setIsOverflowing(isOverflow)
      }
    }

    checkOverflow()

    // Recheck on window resize
    window.addEventListener('resize', checkOverflow)
    return () => window.removeEventListener('resize', checkOverflow)
  }, [text])

  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>
        <span ref={cellRef} className="truncate">
          {text}
        </span>
      </TooltipTrigger>
      {isOverflowing && text && (
        <TooltipContent side="top" className="">
          <span>{text}</span>
        </TooltipContent>
      )}
    </Tooltip>
  )
}

const DocumentPage: React.FC = () => {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { t } = useTranslation()
  const { data: currentUser } = useCurrentUser()

  const {
    openDocuments,
    activeDocumentId,
    setOpenDocument,
    setActiveDocumentId,
    setVersionId,
    setListVersion,
    resetListVersion,
    getVersionId,
    getListVersion,
    versionIdByDocumentId,
    reset,
  } = useDocumentStore()

  const {
    isSidebarCollapsed,
    isRightSidebarCollapsed,
    setSidebarCollapsed,
    setRightSidebarCollapsed,
    toggleCollapseBreakpoint,
  } = useTflViewerStore()

  const {
    clearDocumentState,
    setActiveCommentId,
    setShowCommentDetail,
    setTargetReplyId,
  } = useCommentStore()

  const [activeTab, setActiveTab] = useState<string>(DEFAULT_TAB)
  const [targetCommentId, setTargetCommentId] = useState<string | null>(null)

  const { data: versionListData, refetch: refetchVersionList } = useVersionList(
    activeDocumentId || ''
  )

  const { isXl } = useBreakpoint()

  const isNotificationNavigatedRef = useRef<boolean>(false)
  const isMentionedRef = useRef<boolean>(false)

  // Toggle the collapse state of the sidebar and right sidebar based on the breakpoint
  useEffect(() => {
    toggleCollapseBreakpoint(!isXl)
  }, [isXl, toggleCollapseBreakpoint])

  useEffect(() => {
    return () => {
      setOpenDocument([])
      setActiveDocumentId(null)
    }
  }, [setActiveDocumentId, setOpenDocument])

  const queryDocumentId = searchParams.get('documentId')

  const { data: documentRetrievalData } = useDocumentRetrieval(
    queryDocumentId || ''
  )

  // Get current versionId reactively from Zustand store
  const currentVersionId =
    (activeDocumentId && versionIdByDocumentId[activeDocumentId]) || ''

  const { refetch: refetchDocumentViewAudit } = useDocumentViewAudit(
    activeDocumentId || '',
    currentVersionId
  )

  const previousList = getListVersion(activeDocumentId || '')

  useEffect(() => {
    if (!versionListData || !activeDocumentId) return

    setListVersion(activeDocumentId, versionListData)

    const storedVersionId = getVersionId(activeDocumentId)
    const defaultVersionId = getDefaultVersionId(versionListData)
    const hasNewVersion = versionListData.length > previousList.length
    const isInitialLoad = previousList.length === 0

    // Only update to latest version when a new version is uploaded (not on initial load)
    // This preserves the version set from navigation state (e.g., from comment list)
    if (hasNewVersion && defaultVersionId && !isInitialLoad) {
      setVersionId(activeDocumentId, defaultVersionId)
      return
    }

    // Set default version only if no version is currently stored (initial load case)
    if (!storedVersionId && defaultVersionId) {
      setVersionId(activeDocumentId, defaultVersionId)
    }
  }, [
    versionListData,
    activeDocumentId,
    setListVersion,
    setVersionId,
    getVersionId,
    previousList.length,
  ])

  useEffect(() => {
    const navigationState = location.state as DocumentNavigationState | null
    const context = extractNavigationContext(
      searchParams,
      documentRetrievalData,
      navigationState
    )

    if (!context?.docId || !context?.filename) return

    reset()
    resetListVersion()

    const documentData = { id: context.docId, filename: context.filename }
    setOpenDocument([documentData])
    setActiveDocumentId(context.docId)

    if (context.versionId) {
      setVersionId(context.docId, context.versionId)
    }

    if (context.commentId) {
      setTargetCommentId(context.commentId)
    }

    if (context.replyId) {
      setTargetReplyId(context.replyId)
    }

    if (context.isNotification) {
      isNotificationNavigatedRef.current = true
    }

    if (context.isMentioned) {
      isMentionedRef.current = true
    }

    if (context.isQueryParam) {
      setSearchParams(new URLSearchParams(), { replace: true })
    }
    if (context.isNavState) {
      history.replaceState(null, '', location.pathname)
    }
  }, [
    location.state,
    location.pathname,
    searchParams,
    documentRetrievalData,
    setOpenDocument,
    setActiveDocumentId,
    setVersionId,
    reset,
    resetListVersion,
    setTargetReplyId,
    setSearchParams,
  ])

  // Helper function to scroll to comment in the sidebar
  const scrollToCommentInSidebar = (commentId: string) => {
    const commentListContainer = document.getElementById(
      'comment-list-container'
    )
    const commentElement = commentListContainer?.querySelector(
      `button[data-comment-id="${commentId}"]`
    )

    if (commentElement) {
      commentElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      })
    }
  }

  // Helper function to scroll to comment in the document viewer
  const scrollToCommentInViewer = (commentId: string) => {
    const commentAvatar = document.querySelector(
      `[data-comment-avatar="${commentId}"]`
    )
    if (commentAvatar) {
      commentAvatar.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      })
    }
  }

  // Check if the comment has available mentions
  const checkAvailableMentions = useCallback(
    (comment: ResponseThread) => {
      const mentionedUserIds = extractMentionedUserIds(comment.content)
      return mentionedUserIds?.includes(currentUser?.id || '')
    },
    [currentUser?.id]
  )

  // Handle comment highlighting and scrolling after comment is found
  const handleTargetCommentFound = useCallback(
    (comment: ResponseThread) => {
      if (!comment.id) return

      // Clear target comment id
      setTargetCommentId(null)

      // Check if the comment has available mentions
      if (isNotificationNavigatedRef.current) {
        isNotificationNavigatedRef.current = false

        if (isMentionedRef.current) {
          isMentionedRef.current = false

          // When targetReplyId is set, the mention is in the reply, not the
          // root comment - skip the root-level mention check
          const currentTargetReplyId = useCommentStore.getState().targetReplyId
          if (!currentTargetReplyId) {
            const isAvailableMentions = checkAvailableMentions(comment)
            if (!isAvailableMentions) {
              triggerToast(
                'error',
                t('notifications.error.notificationNotFound')
              )
              return
            }
          }
        }
      }

      // Step 1: Set as active comment (this will trigger highlighting in comment list)
      setActiveCommentId(comment.id)

      // Step 2: Scroll to comment in sidebar after highlight renders
      setTimeout(() => {
        scrollToCommentInSidebar(comment.id)
      }, SCROLL_DELAY_SIDEBAR)

      // Step 3: Scroll to comment in viewer
      setTimeout(() => {
        scrollToCommentInViewer(comment.id)
      }, SCROLL_DELAY_SIDEBAR + SCROLL_DELAY_VIEWER)

      // Step 4: Open detail panel and clear target
      setTimeout(
        () => {
          setShowCommentDetail(true)
          setTargetCommentId(null)
        },
        SCROLL_DELAY_SIDEBAR + SCROLL_DELAY_VIEWER + DETAIL_PANEL_DELAY
      )
    },
    [checkAvailableMentions, setActiveCommentId, setShowCommentDetail, t]
  )

  // Handle error for notification comment not found
  const handleTargetCommentNotFound = useCallback(() => {
    setTargetCommentId(null)
    triggerToast('error', t('notifications.error.notificationNotFound'))
  }, [t])

  useEffect(() => {
    setActiveTab(activeDocumentId || DEFAULT_TAB)
    if (activeDocumentId) {
      document
        .getElementById(activeDocumentId)
        ?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [activeDocumentId, setActiveTab])

  useEffect(() => {
    if (activeDocumentId) refetchVersionList()
  }, [activeDocumentId, refetchDocumentViewAudit, refetchVersionList])

  const handleCloseDocument = (documentId: string) => {
    const index = openDocuments.findIndex(
      (document) => document.id === documentId
    )
    if (index === -1) return
    if (index === 0) {
      setActiveDocumentId(null)
      setActiveTab(DEFAULT_TAB)
    } else {
      const previousDocId = get(openDocuments, [index - 1, 'id'], null)
      setActiveDocumentId(previousDocId)
      setActiveTab(previousDocId || DEFAULT_TAB)
    }

    setOpenDocument(
      openDocuments.filter((document) => document.id !== documentId)
    )
    setListVersion(documentId, [])
    setVersionId(documentId, null)
    clearDocumentState(documentId)
  }

  const handleChooseDocumentTab = (tab: string, documentId?: string) => {
    setActiveTab(tab)
    setActiveDocumentId(documentId ?? null)
  }

  return (
    <>
      {/* TFLs sidebar */}
      <button
        className={cn(
          'absolute left-0 top-12 z-10',
          'h-10 min-w-10 bg-primary border border-l-0 border-solid border-primary rounded-tr-md rounded-br-md px-3.5 py-2.5',
          'flex items-center justify-center gap-1.5 cursor-pointer',
          'transition-all duration-300',
          isSidebarCollapsed ? '' : '-translate-x-full'
        )}
        onClick={() => setSidebarCollapsed(!isSidebarCollapsed)}
      >
        <span className="font-semibold text-sm text-secondary-700">TFLs</span>
        <PanelRightClose className="size-5 text-foreground-fg-quaternary-400 group-hover:text-quaternary-500" />
      </button>
      {/* Tabs */}
      <Tabs value={activeTab} className="h-full overflow-hidden">
        <TabsList className="flex-[0_0_42px]">
          <TabsTrigger
            value={DEFAULT_TAB}
            onClick={() => handleChooseDocumentTab(DEFAULT_TAB)}
          >
            Table of contents
          </TabsTrigger>
          {openDocuments.map((document) => (
            <TabsTrigger
              key={document.id}
              value={document.id}
              onClick={(e) => {
                e.preventDefault()
                handleChooseDocumentTab(document.id, document.id)
              }}
              id={document.id}
            >
              <div className="w-[240px] text-left flex items-center justify-between">
                <TruncatedText text={document.filename} />
                <X
                  size={14}
                  className="size-3.5 cursor-pointer flex-[0_0_14px] hidden group-data-[state=active]:block"
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    handleCloseDocument(document.id)
                  }}
                />
              </div>
            </TabsTrigger>
          ))}
        </TabsList>
        <div className="flex h-[calc(100%-42px)]">
          <div className="flex flex-col flex-1 min-w-0">
            <TabsContent
              value={DEFAULT_TAB}
              className="flex flex-col flex-1 h-full min-w-0 mt-0"
            >
              <DocumentTOC />
            </TabsContent>
            {openDocuments.map((document) => (
              <TabsContent
                key={document.id}
                value={document.id}
                forceMount
                className="flex flex-col flex-1 h-full min-w-0 mt-0"
              >
                <DocumentViewer documentId={document.id} />
              </TabsContent>
            ))}
          </div>
          <CommentSidebar
            isVisible={!!openDocuments.length && activeTab !== DEFAULT_TAB}
            targetCommentId={targetCommentId}
            onTargetCommentFound={handleTargetCommentFound}
            onTargetCommentNotFound={handleTargetCommentNotFound}
          />
          <CommentDetail />
        </div>
        <VersionSelectionDialog />
        <ComparisonLayoutDialog />
      </Tabs>

      {/* Comments sidebar collapse button */}
      <button
        className={cn(
          'right-0 top-12 z-10',
          'h-10 min-w-10 bg-primary border border-r-0 border-solid border-primary rounded-tl-md rounded-bl-md px-3.5 py-2.5',
          'flex items-center justify-center gap-1.5 cursor-pointer',
          'transition-all duration-300',
          !!openDocuments.length && activeTab !== DEFAULT_TAB ? 'absolute' : '',
          isRightSidebarCollapsed ? '' : 'translate-x-full'
        )}
        onClick={() => setRightSidebarCollapsed(!isRightSidebarCollapsed)}
      >
        <MessageSquareMore className="size-5 text-foreground-fg-quaternary-400 group-hover:text-quaternary-500" />
        <span className="font-semibold text-sm text-secondary-700">
          Comments
        </span>
      </button>
    </>
  )
}

export default DocumentPage
